import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHandshake, faSyncAlt, faUsers, faMagic, faRocket, faChartGantt, faChartArea } from '@fortawesome/free-solid-svg-icons';
import Header from '../../component/layout/header';
import Footer from '../../component/layout/footer';
import Navigate from '../../../../util/Navigate';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import IndieSubmenu from '../../component/section/publishers/IndieSubmenu';

function PublisherCrossPromotePage() {
    const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
    const [selectedImage, setSelectedImage] = useState(null);

    const openModal = (imageSrc) => setSelectedImage(imageSrc);
    const closeModal = () => setSelectedImage(null);

    return (
        <>
            <Helmet prioritizeSeoTags>
                <title>Automated Cross-Promotion for Game Developers | Glitch Gaming</title>
                <meta name="description" content="Collaborate with other developers through automated cross-promotion. Grow your audience, increase wishlists, and boost sales like AAA publishers." />
                <meta property="og:image" content="https://www.glitch.fun/assets/images/publishers/cross-promotion-og.jpg" />
            </Helmet>
            <Header />

            {selectedImage && (
                <div className="image-modal-overlay" onClick={closeModal}>
                    <img src={selectedImage} alt="Enlarged preview" className="enlarged-image" />
                </div>
            )}

            {/* Hero Section */}
            <section className="crosspromo-hero">
                <div className="container">
                    <div className="row align-items-center">
                        <div className="col-lg-6 text-center text-lg-start">
                            <h1 className="hero-title pt-5">
                                <span className="gradient-text">AAA-Grade Cross Promotion</span><br />
                                Now Available to All Developers
                            </h1>
                            <p className="hero-subtitle">Automatically grow your audience by collaborating with other games in our network</p>
                            <div className="cta-group">
                                <Link to={Navigate.publishersOnboardingStep1Page()} className="btn btn-primary btn-lg me-3">
                                    <FontAwesomeIcon icon={faRocket} className="me-2" />
                                    Start Free Trial
                                </Link>
                                <Link to={Navigate.demoPage()} className="btn btn-outline-light btn-lg">
                                    Book A Demo
                                </Link>
                            </div>
                        </div>
                        <div className="col-lg-6 hero-visual pt-5">

                            <img src="/assets/images/publishers/cross-promote.png"
                                alt="Cross Promotion Visual"
                                className="hero-image mt-5"
                                onClick={() => openModal('/assets/images/publishers/crosspromo-hero.png')} />
                        </div>
                    </div>
                </div>
            </section>

            {/* Social Proof Section */}
            <section className="social-proof py-5">
                <div className="container">
                    <h2 className="section-title text-center mb-2 text-black">Strategy Used By Major Publishers</h2>
                    <p className='text-black lead text-center mb-5'>Large publishers have an unfair advantage by leveraging multiple social accounts to grow each other and share audiences.
                    But now you can too!</p>
                    <div className="publisher-grid">
                        <div className="publisher-card">
                            <img src="/assets/images/publishers/x_xbox.jpg" alt="Xbox"
                                onClick={() => openModal('/assets/images/publishers/x_xbox.jpg')} />
                            <div className="card-overlay">Microsoft's Cross-Promotion Strategy</div>
                        </div>
                        <div className="publisher-card">
                            <img src="/assets/images/publishers/x_ea.jpg" alt="EA"
                                onClick={() => openModal('/assets/images/publishers/x_ea.jpg')} />
                            <div className="card-overlay">EA's Multi-Title Campaigns</div>
                        </div>
                        <div className="publisher-card">
                            <img src="/assets/images/publishers/x_sega.jpg" alt="Sega"
                                onClick={() => openModal('/assets/images/publishers/x_sega.jpg')} />
                            <div className="card-overlay">Sega's Community Sharing</div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Value Propositions */}
            <section className="value-propositions py-5">
                <div className="container">

                <h2 className="section-title text-center mb-2">Introducing Cross-Promote for Studios and Indies</h2>
                    <p className='lead text-center mb-5'>By collaborating and co-marketing with other developers, everyone benefits from mutual growth by supporting each other's marketing efforts—and it’s all automated!</p>
                    <div className="row g-4">
                        <div className="col-md-4">
                            <div className="value-card">
                                <FontAwesomeIcon icon={faSyncAlt} className="value-icon" />
                                <h3>Automated Sharing</h3>
                                <ul className='text-start'>
                                    <li>Smart post scheduling</li>
                                    <li>Multi-platform distribution</li>
                                    <li>Performance tracking</li>
                                </ul>
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className="value-card">
                                <FontAwesomeIcon icon={faUsers} className="value-icon" />
                                <h3>Collaborative Network</h3>
                                <ul className='text-start'>
                                    <li>Curated developer matches</li>
                                    <li>Audience sharing</li>
                                    <li>Community growth</li>
                                </ul>
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className="value-card">
                                <FontAwesomeIcon icon={faChartGantt} className="value-icon" />
                                <h3>Performance Analytics</h3>
                                <ul className='text-start'>
                                    <li>Real-time metrics</li>
                                    <li>ROI tracking</li>
                                    <li>Engagement insights</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* How It Works */}
            <section className="workflow py-5">
                <div className="container text-black">
                    <h2 className="section-title text-center mb-5 text-black">Simple 3-Step Process</h2>
                    <div className="timeline">
                        <div className="timeline-progress"></div>
                        <div className="row g-4">
                            <div className="col-md-4">
                                <div className="step-card">
                                    <div className="step-number">1</div>
                                    <FontAwesomeIcon icon={faHandshake} className="step-icon" />
                                    <h4 className='text-black'>Connect with Developers</h4>
                                    <p>Find perfect partners through our matching algorithm</p>
                                    <img src="/assets/images/publishers/search.png"
                                        alt="Matching Interface"
                                        onClick={() => openModal('/assets/images/publishers/search.png')} />
                                </div>
                            </div>
                            <div className="col-md-4">
                                <div className="step-card">
                                    <div className="step-number">2</div>
                                    <FontAwesomeIcon icon={faMagic} className="step-icon" />
                                    <h4 className='text-black'>Automate Sharing</h4>
                                    <p>Set your posting schedule and content rules</p>
                                    <img src="/assets/images/publishers/scheduler_results.png"
                                        alt="Scheduler Interface"
                                        onClick={() => openModal('/assets/images/publishers/scheduler_results.png')} />
                                </div>
                            </div>
                            <div className="col-md-4">
                                <div className="step-card">
                                    <div className="step-number">3</div>
                                    <FontAwesomeIcon icon={faChartArea} className="step-icon" />
                                    <h4 className='text-black'>Grow Together</h4>
                                    <p>Watch your audiences expand through mutual support</p>
                                    <img src="/assets/images/publishers/graph_up.png"
                                        alt="Growth Chart"
                                        onClick={() => openModal('/assets/images/publishers/graph_up.png')} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Benefits Section */}
            <section className="benefits py-5">
                <div className="container">
                    <div className="row align-items-center">
                        <div className="col-lg-6">
                            <div className="benefit-card">
                                <h3>Early Access Advantage</h3>
                                <p className="lead">Start building your audience months before launch</p>
                                <img src="/assets/images/publishers/wishlist_years.png"
                                    alt="Wishlist Growth"
                                    className="benefit-image"
                                    onClick={() => openModal('/assets/images/publishers/wishlist_years.png')} />
                                <ul className="benefit-list">
                                    <li>🔄 Consistent community growth</li>
                                    <li>📈 Organic wishlist accumulation</li>
                                    <li>🚀 Strong launch momentum</li>
                                </ul>
                            </div>
                        </div>
                        <div className="col-lg-6">
                            <div className="benefit-card">
                                <h3>Data-Driven Results</h3>
                                <div className="metrics-grid">
                                    <div className="metric-card">
                                        <div className="metric-value">3-5x</div>
                                        <div className="metric-label">Audience Growth</div>
                                    </div>
                                    <div className="metric-card">
                                        <div className="metric-value">40%</div>
                                        <div className="metric-label">Engagement Boost</div>
                                    </div>
                                    <div className="metric-card">
                                        <div className="metric-value">2.8x</div>
                                        <div className="metric-label">ROI Increase</div>
                                    </div>
                                </div>
                                <img src="/assets/images/publishers/retention_chart.png"
                                    alt="Retention Chart"
                                    className="benefit-image"
                                    onClick={() => openModal('/assets/images/publishers/retention_chart.png')} />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Final CTA */}
            <section className="final-cta py-5">
                <div className="container text-center">
                    <h2 className="cta-title">Start Growing Your Audience Today</h2>
                    <p className="cta-subtitle">Join 1,200+ developers already boosting their reach</p>
                    <div className="cta-group">
                        <Link to={Navigate.publishersOnboardingStep1Page()} className="btn btn-primary btn-lg me-3">
                            Begin Free Trial
                        </Link>
                        <Link to={Navigate.demoPage()} className="btn btn-outline-light btn-lg">
                            Book A Demo
                        </Link>
                    </div>
                </div>
            </section>

            <IndieSubmenu />
            <Footer />

            <style jsx>{`
                .crosspromo-hero {
                    padding: 100px 0;
                    background: linear-gradient(135deg, #1a1b2f 0%, #080e37 100%);
                    color: white;
                }

                .hero-title {
                    font-size: 2.8rem;
                    margin-bottom: 1.5rem;
                    line-height: 1.2;
                }

                .gradient-text {
                    background: linear-gradient(90deg, #ff0052 0%, #9400ff 100%);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                }

                .hero-visual {
                    position: relative;
                    padding: 2rem;
                }

                .platform-grid {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 1rem;
                    position: absolute;
                    top: 20%;
                    left: 10%;
                    z-index: 2;
                }

                .platform-icon {
                    width: 60px;
                    height: 60px;
                    object-fit: contain;
                    filter: drop-shadow(0 2px 4px rgba(0,0,0,0.2));
                }

                .hero-image {
                    width: 100%;
                    border-radius: 20px;
                    box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25);
                }

                .social-proof {
                    background: #f8f9ff;
                    padding: 4rem 0;
                }

                
                .publisher-card {
                position: relative;
                border-radius: 12px;
                overflow: hidden;
                transition: transform 0.3s ease;
                aspect-ratio: 9/16; /* Fallback for modern browsers */
                }

                .publisher-image-container {
                position: relative;
                width: 100%;
                padding-bottom: 177.78%; /* 16:9 aspect ratio (9/16 = 0.5625 inverse) */
                }

                .publisher-card img {
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                object-fit: cover;
                }


                /* Optional: Adjust grid layout for better mobile display */
                .publisher-grid {
                display: grid;
                grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
                gap: 1.5rem;
                }

                .card-overlay {
                position: absolute;
                bottom: 0;
                left: 0;
                right: 0;
                background: rgba(0,0,0,0.7);
                color: white;
                padding: 1rem;
                text-align: center;
                font-size: 0.9rem;
                }

                .value-propositions {
                    background: #080e37;
                    color: white;
                    padding: 4rem 0;
                }

                .value-card {
                    background: rgba(255,255,255,0.05);
                    padding: 2rem;
                    border-radius: 12px;
                    text-align: center;
                    transition: all 0.3s ease;
                }

                .value-card:hover {
                    background: rgba(255,255,255,0.1);
                    transform: translateY(-5px);
                }

                .value-icon {
                    font-size: 2.5rem;
                    margin-bottom: 1rem;
                    color: #ff0052;
                }

                .workflow {
                    background: #f8f9ff;
                    padding: 4rem 0;
                }

                .timeline {
                    position: relative;
                    padding: 2rem 0;
                }

                .timeline-progress {
                    position: absolute;
                    top: 50%;
                    left: 0;
                    right: 0;
                    height: 4px;
                    background: rgba(0,0,0,0.1);
                    z-index: 1;
                }

                .step-card {
                    position: relative;
                    z-index: 2;
                    background: white;
                    padding: 2rem;
                    border-radius: 12px;
                    box-shadow: 0 10px 15px -3px rgba(0,0,0,0.1);
                    text-align: center;
                    transition: all 0.3s ease;
                }

                .step-card:hover {
                    transform: translateY(-10px);
                }

                .step-card img {
        height: 240px;
        width: 100%;
        object-fit: contain;
        margin: 1rem 0;
        border-radius: 8px;
        cursor: zoom-in;
    }

    @media (max-width: 992px) {
        .step-card img {
            height: 200px;
        }
    }

    @media (max-width: 768px) {
        .step-card img {
            height: 180px;
        }
    }

                .step-number {
                    width: 50px;
                    height: 50px;
                    background: #ff0052;
                    color: white;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 1.5rem;
                    margin: -40px auto 1rem;
                }

                .benefits {
                    padding: 4rem 0;
                    background: linear-gradient(135deg, #1a1b2f 0%, #080e37 100%);
                    color: white;
                }

                .benefit-card {
                    background: rgba(255,255,255,0.05);
                    padding: 2rem;
                    border-radius: 12px;
                    margin-bottom: 2rem;
                }

                .benefit-image {
                    width: 100%;
                    border-radius: 8px;
                    margin: 1rem 0;
                }

                .metrics-grid {
                    display: grid;
                    grid-template-columns: repeat(3, 1fr);
                    gap: 1rem;
                    margin: 2rem 0;
                }

                .metric-card {
                    background: rgba(255,255,255,0.1);
                    padding: 1.5rem;
                    border-radius: 8px;
                    text-align: center;
                }

                .final-cta {
                    background: linear-gradient(135deg, #ff0052 0%, #9400ff 100%);
                    color: white;
                    padding: 4rem 0;
                    text-align: center;
                }

                @media (max-width: 768px) {
                    .hero-title {
                        font-size: 2rem;
                    }
                    
                    .platform-grid {
                        position: static;
                        margin-bottom: 2rem;
                    }
                }

                .image-modal-overlay {
                position: fixed;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                background: rgba(0, 0, 0, 0.9);
                display: flex;
                justify-content: center;
                align-items: center;
                z-index: 9999;
                padding: 20px;
                cursor: zoom-out;
                }

                .enlarged-image {
                max-width: 100%;
                max-height: 90vh;
                object-fit: contain;
                border-radius: 8px;
                animation: zoomIn 0.3s ease;
                cursor: default;
                }

                @keyframes zoomIn {
                from { transform: scale(0.95); opacity: 0; }
                to { transform: scale(1); opacity: 1; }
                }

                @media (max-width: 768px) {
                .enlarged-image {
                    max-width: 95%;
                    max-height: 80vh;
                }
                }
            `}</style>
        </>
    );
}

export default PublisherCrossPromotePage;